import React, { useState, useEffect } from 'react';
import { Star, Quote, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const DynamicTestimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const testimonials = [
    {
      id: 1,
      name: "Marie L.",
      location: "Bordeaux",
      intervention: "Frelon asiatique",
      rating: 5,
      text: "Intervention ultra rapide ! Nid détruit en 30 minutes, équipe professionnelle et rassurante. Je recommande vivement.",
      time: "Il y a 2 jours",
      verified: true
    },
    {
      id: 2,
      name: "Pierre M.",
      location: "Mérignac", 
      intervention: "Nid de guêpes",
      rating: 5,
      text: "Service exceptionnel ! Même un dimanche soir, ils sont venus détruire le nid. Tarif honnête et travail parfait.",
      time: "La semaine dernière",
      verified: true
    },
    {
      id: 3,
      name: "Sophie D.",
      location: "Pessac",
      intervention: "Frelon européen",
      rating: 5,
      text: "Très satisfaite ! Devis gratuit respecté, intervention propre et conseils préventifs donnés. Merci Frelon33 !",
      time: "Il y a 4 jours",
      verified: true
    },
    {
      id: 4,
      name: "Laurent R.",
      location: "Talence",
      intervention: "Guêpes",
      rating: 5,
      text: "Appelés en urgence pour un nid près de la piscine. Intervention immédiate et sécurisée. Parfait !",
      time: "Hier",
      verified: true
    },
    {
      id: 5,
      name: "Isabelle C.",
      location: "Villenave-d'Ornon",
      intervention: "Frelon asiatique",
      rating: 5,
      text: "Équipe très compétente. Nid de 40cm détruit sans dégâts. Prix raisonnable pour un service de qualité.",
      time: "Il y a 3 jours",
      verified: true
    },
    {
      id: 6,
      name: "Thomas B.",
      location: "Gradignan",
      intervention: "Nid de guêpes",
      rating: 5,
      text: "Intervention le jour même ! Technicien sympa qui explique bien. Aucune guêpe n'est revenue. Top !",
      time: "Il y a 5 jours",
      verified: true
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        setIsAnimating(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const getInterventionColor = (intervention) => {
    if (intervention.toLowerCase().includes('frelon asiatique')) return 'text-red-600 bg-red-100';
    if (intervention.toLowerCase().includes('frelon')) return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  const current = testimonials[currentTestimonial];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Ce que disent nos <span className="text-yellow-600">clients</span>
        </h3>
        <p className="text-gray-600">Plus de 850 interventions réussies en Gironde</p>
      </div>

      {/* Main Testimonial Card */}
      <Card className={`relative overflow-hidden transition-all duration-300 ${isAnimating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row gap-6">
            
            {/* Quote Icon */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                <Quote className="h-8 w-8 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-4">
              
              {/* Rating */}
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  {[...Array(current.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                {current.verified && (
                  <div className="flex items-center space-x-1 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Avis vérifié</span>
                  </div>
                )}
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-lg text-gray-700 leading-relaxed italic">
                "{current.text}"
              </blockquote>

              {/* Author Info */}
              <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-gray-700">{current.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{current.name}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-1 text-gray-500">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">{current.location}</span>
                </div>

                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getInterventionColor(current.intervention)}`}>
                  {current.intervention}
                </div>

                <div className="flex items-center space-x-1 text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">{current.time}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>

        {/* Animated Background Elements */}
        <div className="absolute top-4 right-4 w-2 h-2 bg-yellow-400 rounded-full animate-ping opacity-60"></div>
        <div className="absolute bottom-6 right-8 w-1 h-1 bg-blue-400 rounded-full animate-pulse"></div>
      </Card>

      {/* Testimonial Navigation Dots */}
      <div className="flex justify-center space-x-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsAnimating(true);
              setTimeout(() => {
                setCurrentTestimonial(index);
                setIsAnimating(false);
              }, 300);
            }}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentTestimonial 
                ? 'bg-yellow-500 scale-125' 
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
          <div className="text-2xl font-bold text-green-600">4.9/5</div>
          <div className="text-sm text-green-700">Note moyenne</div>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">850+</div>
          <div className="text-sm text-blue-700">Avis clients</div>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">98%</div>
          <div className="text-sm text-yellow-700">Satisfaction</div>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">24h</div>
          <div className="text-sm text-purple-700">Réactivité moy.</div>
        </div>
      </div>
    </div>
  );
};

export default DynamicTestimonials;